import {ref} from 'vue';
import {useRequestHeaders} from 'nuxt/app';
import {defineStore} from 'pinia';
import type {RouteLocationNormalized} from 'vue-router';
import {escapeHtml} from '@malt/sanitize';
import {readonlyState} from '@malt/utils';

export const usePageSourceStore = defineStore('page-source-store', () => {
    const pageSource = ref<string | null>(null);

    function initPageSourceIfNeeded(url: RouteLocationNormalized) {
        if (!pageSource.value) {
            pageSource.value = determinePageSource(url);
        }
    }

    function determinePageSource(url: RouteLocationNormalized) {
        // Possibly set by any page linking to /signup-client,
        // especially /signup (temporarily /who-are-you), see registration-front:useSignupAttributesPropagation
        const explicitPageSource = url.query.pageSource;
        if (explicitPageSource) {
            return escapeHtml(explicitPageSource);
        }

        // Fallback for cases where where the user ends up directly on /signup-client without passing by
        // /signup (temporarily /who-are-you) before.
        // /!\ We should get rid of these cases, by simply adding the `pageSource` parameter to the /signup-client link
        const requestHeaders = import.meta.server ? useRequestHeaders() : {referer: document.referrer};
        const referrer = requestHeaders.referer || null;
        if (!referrer) {
            return null;
        }

        const referrerUrl = new URL(referrer);

        if (!referrerUrl.pathname) {
            return null;
        }

        if (referrerUrl.pathname === '/project/new') {
            return 'search_brief_submission';
        }

        if (referrerUrl.pathname.match(/customer\/searches\/(?:exec\/)?draft\/.*\/overview/)) {
            return 'brief_submission';
        }

        if (referrerUrl.pathname.startsWith('/c/') || referrerUrl.pathname.startsWith('/a/') || referrerUrl.pathname.startsWith('/s/tags/')) {
            return 'cms_marketing_page';
        }

        if (referrerUrl.pathname.startsWith('/ressources/')) {
            return 'ressource_center';
        }

        if (referrerUrl.pathname.startsWith('/proposal/')) {
            return 'proposal_page';
        }

        return null;
    }

    function addPageSourceParamToUrl(url: string) {
        if (pageSource.value) {
            return `${url}${url.includes('?') ? '&' : '?'}pageSource=${encodeURIComponent(pageSource.value)}`;
        }
        return url;
    }

    return {
        pageSource: readonlyState(pageSource),
        addPageSourceParamToUrl,
        initPageSourceIfNeeded,
    };
});
